// src/services/syncApi.js
import axios from 'axios';
import BASE_URL from '../config';
import accessManager from '../Auth/Access';

const API_URL = `${BASE_URL}`;

// Create axios instance with interceptors for authentication
const syncApiClient = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add authentication headers
syncApiClient.interceptors.request.use(
  (config) => {
    const token = accessManager.getTokens().access_token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
syncApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshed = await accessManager.refreshTokenWithRetry();
        if (refreshed) {
          const newToken = accessManager.getTokens().access_token;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return syncApiClient(originalRequest);
        } else {
          // If refresh fails, logout user
          accessManager.logout();
          throw new Error('Authentication failed');
        }
      } catch (refreshError) {
        accessManager.logout();
        throw refreshError;
      }
    }
    
    return Promise.reject(error);
  }
);

// Sync-related API calls
export const fetchFailedSyncs = async (params) => {
  const response = await syncApiClient.get('/sync/failed/', { params });
  return response.data;
};

export const fetchSyncStatistics = async (params) => {
  const response = await syncApiClient.get('/sync/statistics/', { params });
  return response.data;
};

export const resolveSyncRecord = async (syncId, resolutionData) => {
  const response = await syncApiClient.post(`/sync/resolve/${syncId}/`, resolutionData);
  return response.data;
};

export const fetchAllocationDetails = async (syncId) => {
  const response = await syncApiClient.get(`/sync/failed-allocation/${syncId}/`);
  return response.data;
};
// ============================================================================
// Request Management API calls
// ============================================================================

// Fetch requests with optional filters
export const fetchRequests = async (params) => {
  const response = await syncApiClient.get('/requests/', { params });
  return response.data;
};

// Fetch single request details
export const fetchRequestById = async (requestId) => {
  const response = await syncApiClient.get(`/requests/${requestId}/`);
  return response.data;
};

// Fetch request review context (member balances, details, etc.)
export const fetchRequestReviewContext = async (requestId) => {
  const response = await syncApiClient.get(`/requests/${requestId}/review_context/`);
  return response.data;
};

// Approve a request
export const approveRequest = async (requestId, notes = '') => {
  const response = await syncApiClient.post(`/requests/${requestId}/approve/`, { notes });
  return response.data;
};

// Reject a request
export const rejectRequest = async (requestId, notes) => {
  const response = await syncApiClient.post(`/requests/${requestId}/reject/`, { notes });
  return response.data;
};

// Execute an approved request
export const executeRequest = async (requestId, data = {}) => {
  const response = await syncApiClient.post(`/requests/${requestId}/execute/`, data);
  return response.data;
};

// Cancel a request
export const cancelRequest = async (requestId, reason = '') => {
  const response = await syncApiClient.delete(`/requests/${requestId}/`, { 
    data: { reason } 
  });
  return response.data;
};

// Fetch only pending requests
export const fetchPendingRequests = async (params) => {
  const response = await syncApiClient.get('/requests/pending/', { params });
  return response.data;
};

// Fetch only approved requests
export const fetchApprovedRequests = async (params) => {
  const response = await syncApiClient.get('/requests/approved/', { params });
  return response.data;
};

// Fetch requests ready for execution
export const fetchRequestsForExecution = async (params) => {
  const response = await syncApiClient.get('/requests/for_execution/', { params });
  return response.data;
};

// Fetch request summary/statistics
export const fetchRequestSummary = async (params) => {
  const response = await syncApiClient.get('/requests/summary/', { params });
  return response.data;
};

// Bulk approve multiple requests
export const bulkApproveRequests = async (requestIds, notes = '') => {
  const response = await syncApiClient.post('/requests/bulk-approve/', {
    request_ids: requestIds,
    notes
  });
  return response.data;
};

// Bulk execute multiple requests
export const bulkExecuteRequests = async (requestIds, notes = '') => {
  const response = await syncApiClient.post('/requests/bulk-execute/', {
    request_ids: requestIds,
    notes
  });
  return response.data;
};